import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { toast } from "sonner";
import { Truck, Coffee, Croissant, CakeSlice, Cookie, Pizza, Soup, Printer, ChevronDown, ChevronRight, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import SearchBar, { applyTextFilter } from "@/components/SearchBar";
import { packageSize, packageLabel, toPieces, formatQtyWithPackage, productUnitOptions } from "@/lib/units";
import { printDeliveryNotes } from "@/lib/deliveryNote";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const ICONS = {
  coffee: Coffee,
  croissant: Croissant,
  cake: CakeSlice,
  cookie: Cookie,
  pizza: Pizza,
  soup: Soup,
};

export default function Dispatch() {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [avail, setAvail] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  // rows: { [branch_id]: { count, mode } }
  const [rows, setRows] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Dodací listy / přehledy
  const [dispatches, setDispatches] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [periodType, setPeriodType] = useState("day"); // day | week | month | custom
  const [noteDate, setNoteDate] = useState(todayIso);
  const [periodStart, setPeriodStart] = useState(todayIso);
  const [periodEnd, setPeriodEnd] = useState(todayIso);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [expanded, setExpanded] = useState({});

  const prodWh = warehouses.find((w) => w.type === "production");

  const load = async () => {
    const [p, w, b, a, d] = await Promise.all([
      api.get("/products", { params: { include_deleted: true } }),
      api.get("/warehouses"),
      api.get("/branches"),
      api.get("/stock/product-availability"),
      api.get("/dispatches"),
    ]);
    setProducts(p.data);
    setWarehouses(w.data);
    setBranches(b.data);
    setAvail(a.data);
    setDispatches(d.data);
  };

  useEffect(() => {
    load();
  }, []);

  const availMap = useMemo(() => {
    const m = {};
    avail.forEach((a) => {
      const k = a.product_id;
      if (!m[k]) m[k] = { qty: 0, available: 0, pending: 0, cost: 0 };
      m[k].qty += a.quantity;
      m[k].available += a.available ?? a.quantity;
      m[k].pending += a.pending_dispatch ?? 0;
      m[k].cost = a.avg_unit_cost;
    });
    return m;
  }, [avail]);

  const productsWithStock = products.filter((p) => !p.deleted && (availMap[p.id]?.available || 0) > 0);
  const visible = useMemo(
    () => applyTextFilter(productsWithStock, search, (p) => [p.name]),
    [productsWithStock, search]
  );

  const openTile = (product) => {
    const defaultMode = product.default_dispatch_unit || "piece";
    const initial = {};
    branches.forEach((b) => {
      initial[b.id] = { count: 0, mode: defaultMode };
    });
    setRows(initial);
    setSelected(product);
    setOpen(true);
  };

  const setRow = (branchId, patch) =>
    setRows((r) => ({ ...r, [branchId]: { ...r[branchId], ...patch } }));

  const submit = async () => {
    if (!selected) return;
    const entries = Object.entries(rows).filter(([_, r]) => parseFloat(r.count) > 0);
    if (entries.length === 0) {
      toast.error("Zadejte množství alespoň pro jednu pobočku");
      return;
    }
    const requested = entries.reduce(
      (s, [_, r]) => s + toPieces(selected, r.mode, r.count),
      0
    );
    const dispoz = availMap[selected.id]?.available || 0;
    if (requested > dispoz + 1e-9) {
      toast.error(
        `Součet je ${formatQty(requested, "ks")}, ale dispozice je jen ${formatQty(dispoz, "ks")}`
      );
      return;
    }
    setSubmitting(true);
    try {
      await Promise.all(
        entries.map(([branch_id, r]) =>
          api.post("/dispatches", {
            product_id: selected.id,
            from_warehouse_id: prodWh.id,
            branch_id,
            quantity: toPieces(selected, r.mode, r.count),
            unit_mode: r.mode,
            unit_count: parseFloat(r.count),
          })
        )
      );
      toast.success(`Expedováno na ${entries.length} pobočk${entries.length === 1 ? "u" : "y"} — čeká na potvrzení`);
      setOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  const totalRequested = selected
    ? Object.values(rows).reduce((s, r) => s + toPieces(selected, r.mode, r.count), 0)
    : 0;

  // ---- Dodací listy: filtr podle období a seskupení podle pobočky ----
  const productById = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);
  const branchById = useMemo(() => {
    const m = {};
    branches.forEach((b) => (m[b.id] = b));
    return m;
  }, [branches]);

  // Vypočítej efektivní rozsah dat (ISO YYYY-MM-DD)
  const [effStart, effEnd] = useMemo(() => {
    if (periodType === "day") return [noteDate, noteDate];
    if (periodType === "custom") {
      const a = periodStart || todayIso;
      const b = periodEnd || todayIso;
      return a <= b ? [a, b] : [b, a];
    }
    if (periodType === "week") {
      const ref = new Date(noteDate + "T00:00:00");
      const day = ref.getDay(); // 0=Sun..6=Sat
      const monOffset = day === 0 ? -6 : 1 - day;
      const monday = new Date(ref);
      monday.setDate(ref.getDate() + monOffset);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return [monday.toISOString().slice(0, 10), sunday.toISOString().slice(0, 10)];
    }
    // month
    const ref = new Date(noteDate + "T00:00:00");
    const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const last = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
    return [first.toISOString().slice(0, 10), last.toISOString().slice(0, 10)];
  }, [periodType, noteDate, periodStart, periodEnd, todayIso]);

  const filtered = useMemo(() => {
    return dispatches.filter((d) => {
      const day = (d.created_at || "").slice(0, 10);
      if (day < effStart || day > effEnd) return false;
      if (filterBranch && d.branch_id !== filterBranch) return false;
      if (filterProduct && d.product_id !== filterProduct) return false;
      return true;
    });
  }, [dispatches, effStart, effEnd, filterBranch, filterProduct]);

  // Souhrny
  const stats = useMemo(() => {
    const totalPieces = filtered.reduce((s, d) => s + Number(d.quantity || 0), 0);
    const cost = filtered.reduce((s, d) => s + Number(d.total_cost || 0), 0);
    const statusCounts = filtered.reduce((acc, d) => {
      const s = d.status || "received";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});
    const branchSet = new Set(filtered.map((d) => d.branch_id));
    return {
      count: filtered.length,
      totalPieces,
      cost,
      pending: statusCounts.pending || 0,
      received: (statusCounts.received || 0) + (statusCounts.received_with_issue || 0),
      branches: branchSet.size,
    };
  }, [filtered]);

  // Souhrny per výrobek (pro měsíční přehled)
  const byProduct = useMemo(() => {
    const map = new Map();
    filtered.forEach((d) => {
      if (!map.has(d.product_id)) {
        map.set(d.product_id, { pieces: 0, cost: 0, dispatches: 0 });
      }
      const r = map.get(d.product_id);
      r.pieces += Number(d.quantity || 0);
      r.cost += Number(d.total_cost || 0);
      r.dispatches += 1;
    });
    return Array.from(map.entries())
      .map(([pid, v]) => ({ product_id: pid, name: productById[pid]?.name || "—", ...v }))
      .sort((a, b) => b.pieces - a.pieces);
  }, [filtered, productById]);

  const dayGroups = useMemo(() => {
    const map = new Map();
    filtered.forEach((d) => {
      if (!map.has(d.branch_id)) map.set(d.branch_id, []);
      map.get(d.branch_id).push(d);
    });
    const groups = [];
    map.forEach((items, branch_id) => {
      const branch = branchById[branch_id];
      const totalPieces = items.reduce((s, x) => s + Number(x.quantity || 0), 0);
      const statusCounts = items.reduce((acc, x) => {
        const s = x.status || "received";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});
      groups.push({
        branch_id,
        branchName: branch?.name || "—",
        branchAddress: branch?.address || "",
        items,
        totalPieces,
        statusCounts,
      });
    });
    groups.sort((a, b) => a.branchName.localeCompare(b.branchName, "cs"));
    return groups;
  }, [filtered, branchById]);

  const buildPrintPayload = (group) => ({
    branchName: group.branchName,
    branchAddress: group.branchAddress,
    items: group.items.map((d) => {
      const product = productById[d.product_id];
      return {
        productName: product?.name || "—",
        quantity: Number(d.quantity || 0),
        unitMode: d.unit_mode || "piece",
        unitCount: Number(d.unit_count ?? d.quantity ?? 0),
        packageSize: product ? packageSize(product) : 1,
        packageLabel: product ? packageLabel(product) : "balení",
        note: d.discrepancy_note || "",
      };
    }),
    totalPieces: group.totalPieces,
    dispatchIds: group.items.map((d) => d.id),
  });

  const printOne = (group) => {
    printDeliveryNotes([buildPrintPayload(group)], effStart);
  };
  const printAll = () => {
    if (dayGroups.length === 0) {
      toast.error("Pro vybrané období nejsou žádné expedice");
      return;
    }
    printDeliveryNotes(dayGroups.map(buildPrintPayload), effStart);
  };

  const toggleExpand = (bid) => setExpanded((e) => ({ ...e, [bid]: !e[bid] }));

  return (
    <div data-testid="dispatch-page">
      <PageHeader
        eyebrow="Logistika"
        title="Expedice na pobočky"
        description="Klikněte na výrobek a zadejte, kolik kusů (nebo balení) jde na jednotlivé pobočky. Vyskladnění z výrobního skladu proběhne až po potvrzení příjmu na pobočce."
      />

      {branches.length === 0 && (
        <div className="card-cafe p-8 text-center mb-6">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
            Žádné pobočky
          </h3>
          <p className="text-sm text-[#57534E] mt-1">Pobočky můžete přidat v Administraci.</p>
        </div>
      )}

      {productsWithStock.length === 0 ? (
        <div className="card-cafe p-8 text-center">
          <Truck className="w-8 h-8 mx-auto mb-3 text-[#57534E]" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
            Nic k expedici
          </h3>
          <p className="text-sm text-[#57534E] mt-1">Vyrobte výrobky a vraťte se sem.</p>
        </div>
      ) : (
        <div className="card-cafe p-6">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat výrobek…"
            testid="dispatch-search"
          />
          {visible.length === 0 ? (
            <div className="text-sm text-[#57534E] py-8 text-center">Bez výsledků.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-5">
              {visible.map((p) => {
                const Icon = ICONS[p.icon] || Coffee;
                const a = availMap[p.id];
                return (
                  <button
                    key={p.id}
                    data-testid={`dispatch-tile-${p.id}`}
                    onClick={() => openTile(p)}
                    disabled={!branches.length}
                    className="group flex flex-col items-center justify-center p-7 bg-white border-2 border-[#E5E1D8] rounded-2xl hover:border-[#D4A373] hover:-translate-y-1 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="w-16 h-16 rounded-full bg-[#F9F6F0] grid place-items-center mb-4 group-hover:bg-[#D4A373]/25 transition-colors">
                      <Icon className="w-7 h-7 text-[#3A2D27]" />
                    </div>
                    <div className="text-base font-medium text-[#1C1917] text-center">{p.name}</div>
                    <div className="mt-2 text-xs text-center">
                      <span className="text-[#376A4D] font-medium">
                        {formatQtyWithPackage(a.available, p, "ks")}
                      </span>
                      {a.pending > 0 && (
                        <span className="text-[#9E4747] ml-1">
                          · v expedici {formatQtyWithPackage(a.pending, p, "ks")}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[10px] text-[#57534E]">
                      Ø {formatCZK(a.cost)} / ks
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ───────── Sekce Přehled expedic / Dodací listy ───────── */}
      <div className="card-cafe p-4 sm:p-6 mt-8" data-testid="delivery-notes-section">
        <div className="mb-4">
          <div className="label-eyebrow">Přehled expedic</div>
          <h3 className="text-lg font-semibold mt-1" style={{ fontFamily: "Outfit" }}>
            Filtr a sumace
          </h3>
          <p className="text-sm text-[#57534E] mt-1">
            Vyberte období a další filtry. Pod tabulkou vidíte dodací listy seskupené po pobočce — k tisku jednotlivě i hromadně.
          </p>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <label className="block">
            <div className="text-xs text-[#57534E] mb-1">Období</div>
            <select
              data-testid="period-type"
              className="input-cafe w-full py-1.5"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
            >
              <option value="day">Den</option>
              <option value="week">Týden</option>
              <option value="month">Měsíc</option>
              <option value="custom">Vlastní rozsah</option>
            </select>
          </label>
          {periodType === "custom" ? (
            <>
              <label className="block">
                <div className="text-xs text-[#57534E] mb-1">Od</div>
                <input
                  data-testid="period-start"
                  type="date"
                  className="input-cafe w-full py-1.5"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              </label>
              <label className="block">
                <div className="text-xs text-[#57534E] mb-1">Do</div>
                <input
                  data-testid="period-end"
                  type="date"
                  className="input-cafe w-full py-1.5"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              </label>
            </>
          ) : (
            <label className="block lg:col-span-2">
              <div className="text-xs text-[#57534E] mb-1">
                {periodType === "day" ? "Den" : periodType === "week" ? "Týden (libovolný den v týdnu)" : "Měsíc (libovolný den v měsíci)"}
              </div>
              <input
                data-testid="delivery-note-date"
                type="date"
                className="input-cafe w-full py-1.5"
                value={noteDate}
                onChange={(e) => setNoteDate(e.target.value)}
              />
            </label>
          )}
          <label className="block">
            <div className="text-xs text-[#57534E] mb-1">Pobočka</div>
            <select
              data-testid="filter-branch"
              className="input-cafe w-full py-1.5"
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
            >
              <option value="">— všechny —</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-[#57534E] mb-1">Výrobek</div>
            <select
              data-testid="filter-product"
              className="input-cafe w-full py-1.5"
              value={filterProduct}
              onChange={(e) => setFilterProduct(e.target.value)}
            >
              <option value="">— všechny —</option>
              {products.filter((p) => !p.deleted).map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </label>
        </div>

        {/* Range label + bulk print */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 text-sm text-[#57534E] border-t border-[#E5E1D8] pt-3">
          <div>
            Rozsah: <span className="mono font-medium text-[#1C1917]">{effStart}</span>
            {effStart !== effEnd && <> – <span className="mono font-medium text-[#1C1917]">{effEnd}</span></>}
          </div>
          <button
            data-testid="delivery-note-bulk-print"
            onClick={printAll}
            disabled={dayGroups.length === 0}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            title="Vytiskne všechny dodací listy pro období"
          >
            <Printer className="w-4 h-4" /> Hromadný tisk
          </button>
        </div>

        {/* Sumační karty */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <StatBox label="Expedicí" value={stats.count} testid="stat-count" />
          <StatBox label="Celkem ks" value={formatQty(stats.totalPieces)} testid="stat-pieces" accent="#376A4D" />
          <StatBox label="Hodnota" value={formatCZK(stats.cost)} testid="stat-cost" />
          <StatBox
            label="Stav"
            value={`${stats.received}/${stats.received + stats.pending}`}
            hint={stats.pending > 0 ? `${stats.pending} čeká` : "vše potvrzeno"}
            testid="stat-status"
            accent={stats.pending > 0 ? "#C29329" : "#376A4D"}
          />
        </div>

        {/* Sumace per výrobek (jen pro období > 1 den) */}
        {effStart !== effEnd && byProduct.length > 0 && (
          <div className="mb-5">
            <div className="label-eyebrow mb-2">Sumace per výrobek</div>
            <div className="overflow-x-auto rounded-md border border-[#E5E1D8]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-2 text-xs uppercase tracking-wider text-[#57534E]">Výrobek</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-[#57534E] text-right">Expedicí</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-[#57534E] text-right">Kusů</th>
                    <th className="p-2 text-xs uppercase tracking-wider text-[#57534E] text-right">Hodnota</th>
                  </tr>
                </thead>
                <tbody>
                  {byProduct.map((r) => (
                    <tr key={r.product_id} className="border-t border-[#E5E1D8]">
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2 text-right mono">{r.dispatches}</td>
                      <td className="p-2 text-right mono">{formatQty(r.pieces)}</td>
                      <td className="p-2 text-right mono">{formatCZK(r.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {dayGroups.length === 0 ? (
          <div className="text-sm text-[#57534E] py-8 text-center border border-dashed border-[#E5E1D8] rounded-md">
            Pro vybrané období a filtry nejsou žádné expedice.
          </div>
        ) : (
          <div className="space-y-3">
            {dayGroups.map((g) => {
              const open = !!expanded[g.branch_id];
              const pendingN = g.statusCounts.pending || 0;
              const recvN = (g.statusCounts.received || 0) + (g.statusCounts.received_with_issue || 0);
              return (
                <div
                  key={g.branch_id}
                  className="border border-[#E5E1D8] rounded-md overflow-hidden"
                  data-testid={`delivery-group-${g.branch_id}`}
                >
                  <button
                    onClick={() => toggleExpand(g.branch_id)}
                    className="w-full flex items-center justify-between p-4 bg-[#F9F6F0]/40 hover:bg-[#F9F6F0] transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? (
                        <ChevronDown className="w-4 h-4 text-[#57534E] flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[#57534E] flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{g.branchName}</div>
                        {g.branchAddress && (
                          <div className="text-xs text-[#57534E] truncate">{g.branchAddress}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-sm text-[#57534E]">
                        {g.items.length} položk{g.items.length === 1 ? "a" : g.items.length < 5 ? "y" : "ek"} ·{" "}
                        <span className="text-[#1C1917] font-medium mono">{formatQty(g.totalPieces)} ks</span>
                      </span>
                      <div className="hidden sm:flex items-center gap-1.5 text-xs">
                        {pendingN > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C293291A] text-[#C29329] font-medium">
                            <Clock className="w-3 h-3" /> {pendingN} čeká
                          </span>
                        )}
                        {recvN > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#376A4D1A] text-[#376A4D] font-medium">
                            <CheckCircle2 className="w-3 h-3" /> {recvN} potvrzeno
                          </span>
                        )}
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`delivery-print-${g.branch_id}`}
                        onClick={(e) => { e.stopPropagation(); printOne(g); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); printOne(g); } }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#3A2D27] text-[#3A2D27] hover:bg-[#3A2D27] hover:text-white transition-colors text-sm cursor-pointer"
                      >
                        <Printer className="w-3.5 h-3.5" /> Tisk
                      </span>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-[#E5E1D8] bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F9F6F0]/60">
                          <tr className="text-left">
                            <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Čas</th>
                            <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Výrobek</th>
                            <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Množství</th>
                            <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Stav</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((d) => {
                            const product = productById[d.product_id];
                            const status = d.status || "received";
                            return (
                              <tr key={d.id} className="border-t border-[#E5E1D8]">
                                <td className="p-3 text-[#57534E]">{formatDateTime(d.created_at)}</td>
                                <td className="p-3 font-medium">{product?.name || "—"}</td>
                                <td className="p-3 text-right mono">
                                  {d.unit_mode && d.unit_mode !== "piece" && d.unit_count
                                    ? `${formatQty(d.unit_count)} ${d.unit_mode === "package" ? packageLabel(product) : d.unit_mode} (${formatQty(d.quantity)} ks)`
                                    : formatQtyWithPackage(d.quantity, product, "ks")}
                                </td>
                                <td className="p-3">
                                  {status === "pending" && (
                                    <span className="inline-flex items-center gap-1 text-xs text-[#C29329]">
                                      <Clock className="w-3 h-3" /> Čeká na příjem
                                    </span>
                                  )}
                                  {status === "received" && (
                                    <span className="inline-flex items-center gap-1 text-xs text-[#376A4D]">
                                      <CheckCircle2 className="w-3 h-3" /> Přijato
                                    </span>
                                  )}
                                  {status === "received_with_issue" && (
                                    <span className="inline-flex items-center gap-1 text-xs text-[#9E4747]">
                                      <AlertTriangle className="w-3 h-3" /> Přijato s výhradou
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-[#3A2D27]">
                            <td className="p-3"></td>
                            <td className="p-3 text-right font-medium">Celkem</td>
                            <td className="p-3 text-right mono font-semibold">{formatQty(g.totalPieces)} ks</td>
                            <td className="p-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-2xl bg-white border-[#E5E1D8]"
          data-testid="dispatch-dialog"
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Expedice — {selected?.name}
            </DialogTitle>
            <DialogDescription>
              <span className="text-[#376A4D] font-medium">
                Dispozice: {selected && formatQtyWithPackage(availMap[selected.id]?.available || 0, selected, "ks")}
              </span>
              {selected && (availMap[selected.id]?.pending || 0) > 0 && (
                <span className="text-[#9E4747] ml-2">
                  · v expedici {formatQtyWithPackage(availMap[selected.id].pending, selected, "ks")}
                </span>
              )}
              <br />
              <span className="text-[#57534E]">Zadejte množství u pobočky, kam má jít. Prázdné / 0 se ignoruje.</span>
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-3 pt-1 max-h-[60vh] overflow-y-auto">
              <div className="overflow-hidden rounded-md border border-[#E5E1D8]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F6F0]">
                    <tr className="text-left">
                      <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">
                        Pobočka
                      </th>
                      <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">
                        Množství
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.map((b) => {
                      const r = rows[b.id] || { count: 0, mode: selected.default_dispatch_unit || "piece" };
                      const unitOpts = productUnitOptions(selected);
                      const hasAlt = unitOpts.length > 1;
                      const pcs = toPieces(selected, r.mode, r.count);
                      return (
                        <tr key={b.id} className="border-t border-[#E5E1D8]">
                          <td className="p-3 font-medium">{b.name}</td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-2 flex-wrap">
                              <input
                                data-testid={`dispatch-row-qty-${b.id}`}
                                type="number"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                min="0"
                                step="1"
                                className="input-cafe py-1.5 w-24 text-right"
                                value={r.count}
                                onChange={(e) => setRow(b.id, { count: e.target.value })}
                              />
                              {hasAlt ? (
                                <select
                                  data-testid={`dispatch-row-unit-${b.id}`}
                                  className="input-cafe py-1.5 text-xs"
                                  value={r.mode}
                                  onChange={(e) => setRow(b.id, { mode: e.target.value })}
                                >
                                  {unitOpts.map((o) => (
                                    <option key={o.value} value={o.value}>
                                      {o.value === "piece" ? "ks" : o.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-xs text-[#57534E]">ks</span>
                              )}
                            </div>
                            {hasAlt && r.mode !== "piece" && pcs > 0 && (
                              <div className="text-right text-xs text-[#57534E] mt-0.5">
                                = {pcs} ks
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-[#57534E]">Celkem k expedici</span>
                <span
                  className={`mono font-medium ${
                    totalRequested > (availMap[selected.id]?.available || 0)
                      ? "text-[#9E4747]"
                      : "text-[#376A4D]"
                  }`}
                >
                  {formatQtyWithPackage(totalRequested, selected, "ks")}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="dispatch-confirm"
              onClick={submit}
              disabled={submitting || totalRequested === 0}
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? "Expedujicím…" : "Expedovat"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StatBox({ label, value, hint, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[#E5E1D8] rounded-md p-3 bg-white min-w-0"
    >
      <div className="text-xs uppercase tracking-wider text-[#57534E]">{label}</div>
      <div
        className="text-xl font-semibold mono mt-1 break-words leading-tight"
        style={{ color: accent || "#1C1917" }}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-[#57534E] mt-0.5">{hint}</div>}
    </div>
  );
}
